/**
 * Workflow Drafts List View (Repository Pattern Compliant)
 *
 * Displays user's draft workflows stored in Valkey/Redis with TTL.
 * Data-driven from backend via WorkflowDraftRepository.
 *
 * Architecture:
 * - Storage: Valkey/Redis (temporary, TTL-based)
 * - Repository: WorkflowDraftRepository (audit trail + safety validation)
 * - Actions: Continue, Promote to Saved, Delete
 *
 * Compliance:
 * - Repository Pattern: NO direct GraphQL calls (FERP mandate)
 * - Audit Trail: All operations logged via BaseRepository
 * - Safety Validation: Life-critical operation checks
 *
 * @see CLAUDE.md Line 24: "Repository pattern mandatory"
 * @see useWorkflowDrafts.ts for repository hook implementation
 */

import React from 'react';
import {
    Badge,
    Button,
    Card,
    Group,
    Stack,
    Table,
    Text,
    Title,
    ActionIcon,
    Tooltip,
    Alert,
    Loader
} from '@mantine/core';
import {
    IconPlayerPlay,
    IconRocket,
    IconTrash,
    IconClock,
    IconAlertCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useWorkflowDrafts } from '../hooks/useWorkflowDrafts';
import type { DraftRecord } from '../data/repositories/WorkflowDraftRepository';

interface WorkflowDraftsListViewProps {
    tenant: string;
    onContinueDraft?: (draft: DraftRecord) => void;
    onPromoteToSaved?: (draft: DraftRecord) => void;
    onDeleteDraft?: (draft: DraftRecord) => void;
}

export const WorkflowDraftsListView: React.FC<WorkflowDraftsListViewProps> = ({
    tenant,
    onContinueDraft,
    onPromoteToSaved,
    onDeleteDraft
}) => {
    // ✅ REPOSITORY PATTERN: Use hook that wraps WorkflowDraftRepository
    // This provides audit trails and safety validation
    const { drafts, loading, error, refresh, deleteDraft: deleteDraftRepo } = useWorkflowDrafts({
        kind: undefined, // Load all drafts
        enableAutoRefresh: false,
    });

    // Format timestamp for display
    const formatTimestamp = (ms: number) => {
        return new Date(ms).toLocaleString();
    };

    // Calculate time since last update
    const getTimeSince = (ms: number) => {
        const now = Date.now();
        const diff = now - ms;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const handleContinue = (draft: DraftRecord) => {
        if (onContinueDraft) {
            onContinueDraft(draft);
        } else {
            notifications.show({
                title: 'Continue Draft',
                message: `Loading draft: ${draft.key}`,
                color: 'blue'
            });
        }
    };

    const handlePromote = (draft: DraftRecord) => {
        if (onPromoteToSaved) {
            onPromoteToSaved(draft);
        } else {
            notifications.show({
                title: 'Promote to Saved',
                message: `Promoting draft ${draft.key} to permanent storage`,
                color: 'green'
            });
        }
    };

    const handleDelete = async (draft: DraftRecord) => {
        try {
            // ✅ REPOSITORY PATTERN: Delete via repository (includes audit trail)
            await deleteDraftRepo(draft.key);

            if (onDeleteDraft) {
                onDeleteDraft(draft);
            }

            notifications.show({
                title: 'Draft Deleted',
                message: `Successfully deleted draft: ${draft.key}`,
                color: 'green'
            });
        } catch (err) {
            notifications.show({
                title: 'Delete Failed',
                message: err instanceof Error ? err.message : 'Failed to delete draft',
                color: 'red'
            });
        }
    };

    if (loading) {
        return (
            <Card withBorder>
                <Group justify="center" p="xl">
                    <Loader size="sm" />
                    <Text c="dimmed">Loading drafts from Valkey...</Text>
                </Group>
            </Card>
        );
    }

    if (error) {
        return (
            <Card withBorder>
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error Loading Drafts">
                    {error.message}
                </Alert>
                <Button mt="md" onClick={() => refresh()}>Retry</Button>
            </Card>
        );
    }

    if (!drafts || drafts.length === 0) {
        return (
            <Card withBorder>
                <Stack ta="center" p="xl">
                    <IconClock size={48} opacity={0.3} />
                    <Title order={4}>No Drafts Yet</Title>
                    <Text size="sm" c="dimmed" ta="center">
                        Start creating a workflow to auto-save drafts to Valkey.
                        <br />
                        Drafts are automatically saved every 30 seconds with a 7-day TTL.
                    </Text>
                </Stack>
            </Card>
        );
    }

    return (
        <Card withBorder>
            <Group justify="space-between" mb="md">
                <Group>
                    <Title order={4}>My Draft Workflows</Title>
                    <Badge variant="light" color="blue">
                        Auto-saved (Valkey)
                    </Badge>
                </Group>
                <Button
                    variant="light"
                    size="xs"
                    onClick={() => refresh()}
                >
                    Refresh
                </Button>
            </Group>

            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Workflow Key</Table.Th>
                        <Table.Th>Owner</Table.Th>
                        <Table.Th>Last Updated</Table.Th>
                        <Table.Th>Updated By</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {drafts.map((draft) => (
                        <Table.Tr key={draft.key}>
                            <Table.Td>
                                <Group gap="xs">
                                    <Text fw={500}>{draft.key}</Text>
                                    {draft.deleted && (
                                        <Badge size="xs" color="red">
                                            Deleted
                                        </Badge>
                                    )}
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{draft.owner || 'Unknown'}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Group gap="xs">
                                    <Text size="sm">{formatTimestamp(draft.updatedMs)}</Text>
                                    <Badge size="xs" variant="light" color="gray">
                                        {getTimeSince(draft.updatedMs)}
                                    </Badge>
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{draft.updatedBy}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Group gap="xs" justify="flex-end">
                                    <Tooltip label="Continue working on this draft">
                                        <ActionIcon
                                            variant="light"
                                            color="blue"
                                            onClick={() => handleContinue(draft)}
                                            disabled={draft.deleted}
                                        >
                                            <IconPlayerPlay size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Promote to saved (permanent storage)">
                                        <ActionIcon
                                            variant="light"
                                            color="green"
                                            onClick={() => handlePromote(draft)}
                                            disabled={draft.deleted}
                                        >
                                            <IconRocket size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Delete draft">
                                        <ActionIcon
                                            variant="light"
                                            color="red"
                                            onClick={() => handleDelete(draft)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>

            <Text size="xs" c="dimmed" mt="sm">
                Showing {drafts.length} draft{drafts.length !== 1 ? 's' : ''} • Auto-saved to Valkey with 7-day TTL
            </Text>
        </Card>
    );
};
